/**
 * Screen-space stacking for numbered map badges (Story HUD events, route
 * sequence). Badges whose projected positions collide are merged into one
 * pill that reads "10–11" / "14·15", coloured by the dominant kind with a ring
 * in the other kind's colour. Zooming in splits them again. Numbers are never
 * changed - the pill just lists the members' numbers.
 */

export type StackableBadge = {
  /** [lng, lat] */
  lngLat: [number, number];
  seq: number;
  kind: string;
  color: string;
  /** Extra properties copied onto the feature (first member of a stack wins). */
  extra?: Record<string, unknown>;
};

export type StackOptions = {
  /** Badges closer than this many screen px are stacked (default 22). */
  px?: number;
  /** Draw-order rank per kind; higher wins the stack's colour and anchor (default: all 0). */
  rank?: (kind: string) => number;
  /** Radius multiplier per kind against the 9 / 11.5 / 13.5 px base at z8 / z14 / z18 (default 0.82). */
  radius?: (kind: string) => number;
  /** Kinds whose position should anchor a mixed stack (e.g. dwells, so the pill stays on the ring). */
  anchorKinds?: ReadonlySet<string>;
};

/** "10–11" for a consecutive run, "10·12" for a couple of scattered numbers, "10…15" otherwise. */
export function stackLabel(seqs: number[]): string {
  const s = [...seqs].sort((a, b) => a - b);
  if (s.length === 1) return String(s[0]);
  const consecutive = s.every((v, i) => i === 0 || v === s[i - 1] + 1);
  if (consecutive) return `${s[0]}–${s[s.length - 1]}`;
  if (s.length <= 3) return s.join('·');
  return `${s[0]}…${s[s.length - 1]}`;
}

export type StackedBadgeProps = {
  label: string;
  seq: number;
  seqs: number[];
  count: number;
  kind: string;
  color: string;
  ring: string;
  ringW: number;
  r8: number;
  r14: number;
  r18: number;
  tScale: number;
  sort: number;
} & Record<string, unknown>;

/**
 * Group badges by projected screen distance and emit one GeoJSON point per
 * stack. `project` should be `map.project([lng, lat])` (returns null when the
 * map is not ready - badges then stay unstacked).
 */
export function stackBadges(
  badges: StackableBadge[],
  project: (lngLat: [number, number]) => { x: number; y: number } | null,
  opts: StackOptions = {},
): GeoJSON.FeatureCollection<GeoJSON.Point, StackedBadgeProps> {
  const px = opts.px ?? 22;
  const rank = opts.rank ?? (() => 0);
  const radius = opts.radius ?? (() => 0.82);
  const anchorKinds = opts.anchorKinds;

  type Group = { members: StackableBadge[]; x: number; y: number };
  const groups: Group[] = [];
  for (const b of badges) {
    let p: { x: number; y: number } | null = null;
    try {
      p = project(b.lngLat);
    } catch {
      p = null;
    }
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
      const g = groups.find((g) => Math.hypot(g.x - p!.x, g.y - p!.y) < px);
      if (g) {
        g.members.push(b);
        continue;
      }
      groups.push({ members: [b], x: p.x, y: p.y });
    } else {
      groups.push({ members: [b], x: NaN, y: NaN });
    }
  }

  const features = groups.map((g): GeoJSON.Feature<GeoJSON.Point, StackedBadgeProps> => {
    const top = [...g.members].sort((a, b) => rank(b.kind) - rank(a.kind) || a.seq - b.seq)[0];
    const other = g.members.find((x) => x.kind !== top.kind);
    const label = stackLabel(g.members.map((x) => x.seq));
    const anchor = (anchorKinds && g.members.find((x) => anchorKinds.has(x.kind))) ?? g.members[0];
    // Radius grows with label width so "10–11" sits inside the disc with room to
    // spare: bold text runs ~0.6em per character, and the disc has to cover the
    // badges it replaced (otherwise the text spills onto the markers beneath).
    const widthScale = g.members.length > 1 ? 1 + Math.max(0, label.length - 1) * 0.34 : 1;
    const base = radius(top.kind);
    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: anchor.lngLat },
      properties: {
        ...(top.extra ?? {}),
        label,
        seq: top.seq,
        seqs: g.members.map((x) => x.seq),
        count: g.members.length,
        kind: top.kind,
        color: top.color,
        ring: other ? other.color : '#ffffff',
        ringW: other ? 2.5 : base >= 1 ? 2 : 1.5,
        r8: 9 * base * widthScale,
        r14: 11.5 * base * widthScale,
        r18: 13.5 * base * widthScale,
        tScale: g.members.length > 1 ? 0.88 : 1,
        sort: rank(top.kind) + g.members.length,
      },
    };
  });
  return { type: 'FeatureCollection', features };
}

/** Mapbox paint/layout fragments shared by every stacked-badge layer pair. */
export const STACKED_BADGE_CIRCLE_PAINT = {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, ['get', 'r8'], 14, ['get', 'r14'], 18, ['get', 'r18']],
  'circle-color': ['get', 'color'],
  'circle-stroke-color': ['get', 'ring'],
  'circle-stroke-width': ['get', 'ringW'],
} as const;

export const STACKED_BADGE_TEXT_LAYOUT = {
  'text-field': ['get', 'label'],
  'text-size': [
    'interpolate', ['linear'], ['zoom'],
    8, ['*', 10, ['get', 'tScale']],
    14, ['*', 12.5, ['get', 'tScale']],
    18, ['*', 14.5, ['get', 'tScale']],
  ],
  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  'text-allow-overlap': true,
  'text-ignore-placement': true,
  'symbol-sort-key': ['get', 'sort'],
} as const;
